import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAppLang } from '../../../app/features/lang/providers/AppLangProvider';
import INewsArticle from '../interfaces/INewsArticle';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import langNewsArticle from '../langs/langNewsArticle';

const loadSimNews = async (lang: string): Promise<INewsArticle[]> => {
	try {
		const data = (await axios.get(`https://www.sienna-im.com/wp-json/sienna/news/${lang}`, {
			headers: {
				'Content-Type': 'application/json',
			},
		})) as {
			data: {
				status: string;
				wp_id: string;
				title: string;
				permalink: string;
				creation: Date;
				services: string[];
				image: string;
			}[];
		};

		const publishedArticles = data?.data.filter((a) => a.status === 'publish') ?? [];

		const res: INewsArticle[] =
			publishedArticles.map((a) => ({
				id: a.wp_id,
				title: a.title,
				permalink: a.permalink,
				creation: a.creation,
				services: a.services,
				image: a.image,
			})) ?? [];

		return res;
	} catch (e) {
		return [];
	}
};

const loadSgNews = async (lang: string, t: TFunction): Promise<INewsArticle[]> => {
	try {
		const data = (await axios.get(`https://www.sienna-gestion.com/articles`)) as {
			data: {
				uuid: string;
				title: string;
				field_titre_en: string;
				field_categories_d_articles:
					| 'Actualités'
					| 'Newsletters'
					| 'Communiqués de presse'
					| 'Évolutions réglementaires';
				created: Date;
				view_node: string;
				body: string;
				image: string;
			}[];
		};
		const publishedArticles = data?.data ?? [];
		const res: INewsArticle[] =
			publishedArticles.map((a) => ({
				id: a.uuid,
				title: lang === 'fr' ? a.title : a.field_titre_en || a.title,
				permalink: a.view_node,
				creation: a.created,
				services: [t(langNewsArticle[a.field_categories_d_articles]?.tag)],
				image: a.image,
			})) ?? [];
		return res;
	} catch (e) {
		return [];
	}
};

const useNewsArticles = () => {
	const appLang = useAppLang();
	const { t } = useTranslation();

	return useQuery<INewsArticle[], Error>(
		['newArticles', appLang.language],
		async () => {
			const simNews = await loadSimNews(appLang.language);
			const sgNews = await loadSgNews(appLang.language, t);
			return [...simNews, ...sgNews];
		},
		{
			enabled: !!appLang && !!appLang.language,
		},
	);
};

export default useNewsArticles;
